import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useResolvedVessel } from "@/lib/nav";

const TYPE_LABEL = { pool: "Pool", hot_tub: "Hot tub" } as const;

/**
 * The pool the bar is about, and one tap to any other — or to adding or
 * importing one.
 *
 * Always rendered where there is at least one pool: with one, the list says
 * so and offers to add another, which is how an owner learns the control
 * exists before they need it. Outside a pool's own screens it shows the
 * pool you last looked at, which is what the bar's links point to.
 */
export default function VesselSwitcher() {
  const { id, vessels } = useResolvedVessel();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: PointerEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (vessels.length === 0) return null;
  const current = vessels.find((v) => v.id === id);
  const name = current?.name ?? "Your pool";

  return (
    <div ref={root} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch pool. Current: ${name}`}
        className="flex max-w-36 items-center gap-1 rounded-md border border-(--color-border-subtle) px-2.5 py-1.5 text-sm font-medium hover:bg-(--color-surface-sunken) sm:max-w-56"
      >
        <span className="truncate">{name}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden className="shrink-0 text-(--color-ink-faint)">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div role="menu" aria-label="Your pools" className="card absolute right-0 z-30 mt-1 w-64 overflow-hidden py-1 shadow-lg">
          {vessels.map((vessel) => {
            const active = vessel.id === id;
            return (
              <button
                key={vessel.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setOpen(false);
                  navigate(`/v/${vessel.id}`);
                }}
                className={[
                  "flex w-full items-baseline justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-(--color-surface-sunken)",
                  active ? "font-semibold" : "",
                ].join(" ")}
              >
                <span className="truncate">{vessel.name}</span>
                <span className="unit shrink-0">{TYPE_LABEL[vessel.vessel_type]}</span>
              </button>
            );
          })}
          <Link
            to="/add"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block border-t border-(--color-border-subtle) px-4 py-2.5 text-sm text-(--color-ink-muted) hover:bg-(--color-surface-sunken)"
          >
            Add a pool or hot tub
          </Link>
          <Link
            to="/import"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-(--color-ink-muted) hover:bg-(--color-surface-sunken)"
          >
            Import one from a file
          </Link>
        </div>
      )}
    </div>
  );
}
